import { useState } from "react";
import { useLocation, useNavigate, useSearchParams, Link } from "react-router";
import { useUserInfoCollected } from "../dashboard/user-info";
import { LocalDeck, useGetUserStateAndAlign } from "@/lib/decks-utils/utils";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import {
  FolderClosedIcon,
  FolderOpenIcon,
  HouseIcon,
  ChevronDown,
} from "lucide-react";
import {
  DropdownDeckDialog,
  DropdownFolderDialog,
  NewFolderDialog,
} from "../folder/dialogs";
import { Separator } from "@/components/ui/separator";
import {
  HelpDialog,
  LeaveFeedbackDialog,
  ShareWithFriendsDialog,
} from "@/components/sidebar/components";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AICreditDisplay } from "../folder/ai-credits";
import { Folder } from "@/database/db";
import { getCustomizedColor } from "../folder/folder-color-utils";
import { AddDeckIcon } from "../icons/add-deck";
import { DeckIcon } from "../icons/deck";
import { usePostHog } from "posthog-js/react";
export const availableColors = [
  "hover:bg-violet-200",
  "hover:bg-yellow-200",
  "hover:bg-orange-200",
  "hover:bg-blue-200",
  "hover:bg-sky-200",
  "hover:bg-teal-200",
  "hover:bg-green-200",
  "hover:bg-lime-200",
  "bg-violet-300",
  "bg-yellow-300",
  "bg-orange-300",
  "bg-blue-300",
  "bg-sky-300",
  "bg-teal-300",
  "bg-green-300",
  "bg-lime-300",
  "bg-violet-400",
  "bg-yellow-400",
  "bg-orange-400",
  "bg-blue-400",
  "bg-sky-400",
  "bg-teal-400",
  "bg-green-400",
  "bg-lime-400",
  "bg-violet-400/20",
  "bg-yellow-400/20",
  "bg-orange-400/20",
  "bg-blue-400/20",
  "bg-sky-400/20",
  "bg-teal-400/20",
  "bg-green-400/20",
  "bg-lime-400/20",
  "bg-gray-400/20",
  "bg-violet-100",
  "bg-yellow-100",
  "bg-orange-100",
  "bg-blue-100",
  "bg-sky-100",
  "bg-teal-100",
  "bg-green-100",
  "bg-lime-100",
  "bg-gray-100",
  "hover:border-red-400",
  "hover:bg-red-50",
  "group-hover:border-red-100",
  "hover:border-gray-400",
  "hover:bg-gray-50",
  "group-hover:border-gray-100",
  "hover:border-lime-400",
  "hover:bg-lime-50",
  "group-hover:border-lime-100",
  "hover:border-green-400",
  "hover:bg-green-50",
  "group-hover:border-green-100",
  "hover:border-teal-400",
  "hover:bg-teal-50",
  "group-hover:border-teal-100",
  "hover:border-sky-400",
  "hover:bg-sky-50",
  "group-hover:border-sky-100",
  "hover:border-blue-400",
  "hover:bg-blue-50",
  "group-hover:border-blue-100",
  "hover:border-orange-400",
  "hover:bg-orange-50",
  "group-hover:border-orange-100",
  "hover:border-yellow-400",
  "hover:bg-yellow-50",
  "group-hover:border-yellow-100",
  "hover:border-violet-400",
  "hover:bg-violet-50",
  "group-hover:border-violet-100",
  "hover:border-gray-400",
  "hover:bg-gray-50",
  "group-hover:border-gray-100",
];

const getDeckNameFromPathname = (pathname: string) => {
  if (!pathname.startsWith("/deck")) {
    return null;
  }

  const deckName = pathname.split("/").pop();
  if (!deckName) {
    return null;
  }

  return decodeURIComponent(deckName);
};

const getFolderIdForDeck = (pathname: string, decks: LocalDeck[]) => {
  const deckName = getDeckNameFromPathname(pathname);
  if (!deckName) {
    return null;
  }

  const deck = decks.find((deck) => deck.name === deckName);
  if (!deck) {
    return null;
  }
  return deck.folder;
};

type FolderWithChildren = Folder & { children: FolderWithChildren[] };

const isChildFolder = (
  folder: FolderWithChildren,
  activeFolderId: string
): boolean => {
  if (folder.children.length === 0) {
    return false;
  }

  if (folder.children.some((child) => child.id === activeFolderId)) {
    return true;
  }

  // Recursively check each child's children
  return folder.children.some((child) => isChildFolder(child, activeFolderId));
};

const FolderSubtree = ({
  folder,
  isCollapsed,
  onClickFolder,
  activeFolderId,
  localDecks,
  folders,
  hideDecks = false,
  expandAll = false,
  level,
}: {
  folder: FolderWithChildren;
  isCollapsed: boolean;
  onClickFolder: (folderId: Folder["id"] | null) => void;
  activeFolderId: string;
  localDecks: LocalDeck[];
  folders: Folder[];
  hideDecks?: boolean;
  expandAll?: boolean;
  level: number;
}) => {
  const pathname = useLocation().pathname;
  const navigate = useNavigate();
  const isActiveFolder =
    activeFolderId === folder.id || isChildFolder(folder, activeFolderId);

  const [isFolderOpen, setIsFolderOpen] = useState(isActiveFolder);

  return (
    <>
      <FolderSidebarButton
        folder={folder}
        isCollapsed={isCollapsed}
        onClickFolder={() => {
          onClickFolder?.(folder.id);
          setIsFolderOpen(true);
        }}
        selectedFolderId={activeFolderId}
        key={folder.id}
        folders={folders}
        localDecks={localDecks}
        isFolderOpen={isFolderOpen}
        hideOptions={expandAll}
        setIsFolderOpen={setIsFolderOpen}
      />
      {(expandAll || isFolderOpen) && folder.children.length > 0 ? (
        folder.children.map((child, index) => {
          return (
            <FolderSidebarOptionButton
              key={child.id}
              folder={folder}
              folders={folders}
              isCollapsed={isCollapsed}
              onClickFolder={(folderId) => onClickFolder?.(folderId)}
              activeFolderId={activeFolderId}
              localDecks={localDecks}
              hideDecks={hideDecks}
              expandAll={expandAll}
              level={level}
              child={child}
              index={index}
            />
          );
        })
      ) : (
        <></>
      )}
      {!hideDecks && isFolderOpen ? (
        localDecks
          .filter(
            (deck) =>
              deck.folder === folder.id ||
              (folder.id === "unassigned" && !deck.folder)
          )
          .map((deck) => {
            return (
              <DeckSidebarButton
                deck={deck}
                folder={folder}
                folders={folders}
                level={level}
                isCollapsed={isCollapsed}
                pathname={pathname}
                navigate={navigate}
                isFirstChild={folder.children.length === 0}
                key={deck.name}
              />
            );
          })
      ) : (
        <></>
      )}
    </>
  );
};

const FolderSidebarOptionButton = ({
  folder,
  folders,
  isCollapsed,
  onClickFolder,
  activeFolderId,
  localDecks,
  hideDecks,
  expandAll,
  level,
  child,
  index,
}: {
  folder: FolderWithChildren;
  folders: Folder[];
  isCollapsed: boolean;
  onClickFolder: (folderId: Folder["id"] | null) => void;
  activeFolderId: string;
  localDecks: LocalDeck[];
  hideDecks: boolean;
  expandAll: boolean;
  level: number;
  child: FolderWithChildren;
  index: number;
}) => {
  const isFirstChild = index === 0;

  return (
    <div
      className={cn("relative", isCollapsed ? "" : "pl-6 w-full")}
      key={child.name}
    >
      {!isCollapsed ? (
        <>
          {Array.from({ length: level + 1 }).map((_, idx) => {
            return (
              <div
                key={level + ":" + idx}
                className={cn(
                  idx === level
                    ? "absolute left-4 w-3 h-12 border-l border-b rounded-bl-sm border-red-300 z-10"
                    : "absolute left-[-32px] !top-[-20px] w-2 h-12 border-l z-10",
                  `border-${folder.color}-300`,
                  isFirstChild && idx === level && "h-7"
                )}
                style={{
                  left: `${20 - (level - idx) * 24}px`,
                  top: isFirstChild ? "-6px" : "-26px",
                }}
              ></div>
            );
          })}
        </>
      ) : (
        <></>
      )}
      <FolderSubtree
        folder={child}
        folders={folders}
        isCollapsed={isCollapsed}
        onClickFolder={(folderId) => onClickFolder?.(folderId)}
        activeFolderId={activeFolderId || "unassigned"}
        key={child.id}
        localDecks={localDecks}
        hideDecks={hideDecks}
        expandAll={expandAll}
        level={level + 1}
      />
    </div>
  );
};

const DeckSidebarButton = ({
  deck,
  folder,
  folders,
  level,
  isCollapsed,
  pathname,
  navigate,
  isFirstChild,
}: {
  deck: LocalDeck;
  folder: Folder;
  folders: Folder[];
  level: number;
  isCollapsed: boolean;
  pathname: string;
  navigate: (path: string) => void;
  isFirstChild: boolean;
}) => {
  const [isActionsVisible, setIsActionsVisible] = useState(false);

  return (
    <div
      className={cn("relative", isCollapsed ? "" : "pl-6 w-full")}
      key={deck.name}
    >
      {Array.from({ length: level + 1 }).map((_, idx) => (
        <div
          key={idx}
          className={cn(
            idx === level
              ? "absolute w-3 h-12 border-l border-b rounded-bl-sm z-10"
              : "absolute w-3 h-12 border-l-3 z-10",
            `border-${folder.color}-300`,
            isFirstChild && idx === level && "h-7"
          )}
          style={{
            left: `${20 - (level - idx) * 24}px`,
            top: isFirstChild ? "-6px" : "-26px",
          }}
        ></div>
      ))}
      <Button
        variant="strokeSecondarySidebar"
        size="sm"
        className={cn(
          "w-full p-2 h-10 text-xs items-center justify-start rounded-sm group",
          getCustomizedColor(
            deck.color,
            {
              weight: 100,
              pseudoClass: "focus",
            },
            "bg"
          ),
          getDeckNameFromPathname(pathname) === deck.name && [
            getCustomizedColor(deck.color, { weight: 100 }, "bg"),
            getCustomizedColor(deck.color, {}, "border"),
          ],
          "hover:bg-secondary-100",
          "rounded-lg"
        )}
        onClick={() => {
          navigate(`/deck/${encodeURIComponent(deck.name)}`);
        }}
      >
        <div
          className={cn(
            `rounded-md w-7 h-7 flex-none flex items-center justify-center text-title-950`,
            "group-hover:border group-hover:border-secondary-200",
            "group-focus:border",
            getCustomizedColor(
              deck.color,
              {
                weight: 800,
                pseudoClass: "group-focus",
              },
              "border"
            )
          )}
        >
          <DeckIcon
            className={cn(
              "h-5 w-5 flex-shrink-0 z-20",
              getCustomizedColor(deck.color, {}, "fill"),
              getCustomizedColor(deck.color, { weight: 800 }, "text"),
              `text-${deck.color}-800`
            )}
          />
        </div>
        {!isCollapsed && (
          <span className="text-body-600 text-xs font-bold min-w-0 overflow-hidden text-ellipsis whitespace-nowrap">
            {deck.displayName}
          </span>
        )}
        <div
          className={cn(
            "absolute right-2 group-hover:bg-secondary-100 flex-row items-center gap-2",
            isActionsVisible ? "opacity-100" : "opacity-0",
            "group-hover:opacity-100 transition-opacity",
            "z-[1000]"
          )}
        >
          <DropdownDeckDialog
            deck={deck}
            folders={folders}
            iconSize="icon2xs"
            onOpenChange={setIsActionsVisible}
          />
        </div>
      </Button>
    </div>
  );
};

export const FoldersSidebar = ({
  onClickFolder,
  className = "",
}: {
  onClickFolder?: (folderId: Folder["id"] | null) => void;
  className?: string;
}) => {
  const posthog = usePostHog();
  const navigate = useNavigate();
  const pathname = useLocation().pathname;

  const [params] = useSearchParams();
  const { folders, localDecks } = useGetUserStateAndAlign();
  const isUserInfoCollected = useUserInfoCollected();

  if (!folders || !localDecks || !isUserInfoCollected) {
    return <></>;
  }

  const folderId =
    params.get("folder") ||
    (getFolderIdForDeck(pathname, localDecks) as string) ||
    null;

  return (
    <>
      <TooltipProvider>
        <div
          className={cn(
            "flex flex-col bg-secondary-50 justify-between h-full p-4 transition-transform duration-300 w-64",
            "w-64",
            className
          )}
        >
          <div className="flex flex-col gap-1 flex-1 overflow-y-auto">
            <div className="flex flex-col gap-0 items-center">
              <Button
                variant="strokeSecondarySidebar"
                size="sm"
                className={cn(
                  "w-full justify-start text-xs",
                  pathname === "/dashboard" && folderId === null
                    ? "bg-secondary-200"
                    : ""
                )}
                onClick={() => {
                  onClickFolder?.(null);
                  navigate("/dashboard");
                }}
              >
                <HouseIcon className={cn("w-icon-md h-icon-md text-base")} />
                <span className="block">Home</span>
              </Button>
              <Link
                to="/add"
                className="w-full justify-start text-xs"
                onClick={() => posthog.capture("New-Deck-From-Sidebar")}
              >
                <Button
                  size="sm"
                  variant="strokeSecondarySidebar"
                  className="w-full justify-start text-xs"
                >
                  <AddDeckIcon className="h-icon-md w-icon-md" />
                  <span
                    className={cn("text-body-600 text-xs font-bold", "block")}
                  >
                    Add deck
                  </span>
                </Button>
              </Link>
              <Separator className=" border-secondary-100 my-4" />
              <div
                className={cn(
                  "flex flew-row items-center w-full",
                  "justify-between"
                )}
              >
                <span className="text-body-600 text-xs font-bold">Folders</span>

                <NewFolderDialog />
              </div>
            </div>
            <div>
              <FullFoldersSubtree
                folders={folders}
                localDecks={localDecks}
                folderId={folderId}
                onClickFolder={onClickFolder}
              />
            </div>
          </div>
          <div className={cn("flex flex-col gap-2 flex-shrink-0")}>
            <Separator className="border-secondary-100" />
            <ShareWithFriendsDialog />
            <HelpDialog isCollapsed={false} />
            <LeaveFeedbackDialog isCollapsed={false} />
            <Separator className="border-secondary-100" />
            <div className="w-full flex flex-col gap-2">
              <AICreditDisplay isCollapsed={false} isShowWhenPremium={false} />
            </div>
          </div>
        </div>
      </TooltipProvider>
    </>
  );
};

export const FullFoldersSubtree = ({
  folders,
  localDecks,
  folderId,
  onClickFolder,
  hideDecks = false,
  expandAll = false,
}: {
  folders: Folder[];
  localDecks: LocalDeck[];
  folderId: Folder["id"] | null;
  onClickFolder?: (folderId: Folder["id"] | null) => void;
  hideDecks?: boolean;
  expandAll?: boolean;
}) => {
  const getSidebarTree = () => {
    const decksWithoutFolder = localDecks.filter(
      (deck) => !deck.folder || deck.folder === "unassigned"
    );

    let foldersWithUnassigned = [...folders];
    if (decksWithoutFolder.length > 0) {
      foldersWithUnassigned.push({
        id: "unassigned",
        name: "Decks without folder",
        color: "gray",
      });
    }

    const folderFatherChildrenMap: Record<string, FolderWithChildren[]> = {};

    for (const folder of foldersWithUnassigned) {
      if (folder.parentFolder && folder.parentFolder !== "unassigned") {
        folderFatherChildrenMap[folder.parentFolder] = [
          ...(folderFatherChildrenMap[folder.parentFolder] || []),
          { ...folder, children: [] },
        ];
      }
    }

    const foldersWithoutParent = foldersWithUnassigned.filter(
      (folder) => !folder.parentFolder || folder.parentFolder === "unassigned"
    );

    const getChildrenSubtree = (folder: Folder): FolderWithChildren[] => {
      const children = folderFatherChildrenMap[folder.id] || [];
      return children.map((child) => {
        return { ...child, children: getChildrenSubtree(child) };
      });
    };

    return foldersWithoutParent.map((folder) => {
      return {
        ...folder,
        children: (folderFatherChildrenMap[folder.id] || []).map((folder) => ({
          ...folder,
          children: getChildrenSubtree(folder),
        })),
      };
    });
  };

  const sidebarTree = getSidebarTree();

  return (
    <>
      {sidebarTree.map((folder) => {
        return (
          <FolderSubtree
            key={folder.id}
            folder={folder}
            isCollapsed={false}
            onClickFolder={(folderId) => onClickFolder?.(folderId)}
            activeFolderId={folderId || "unassigned"}
            localDecks={localDecks}
            folders={folders}
            hideDecks={hideDecks}
            expandAll={expandAll}
            level={0}
          />
        );
      })}
    </>
  );
};

export const FolderColoredIcon = ({
  color,
  isOpen = false,
  className,
}: {
  color: Folder["color"];
  isOpen?: boolean;
  className?: string;
}) => {
  const Icon = isOpen ? FolderOpenIcon : FolderClosedIcon;

  return (
    <Icon
      className={cn(
        "h-5 w-5 z-20 flex-shrink-0",
        getCustomizedColor(color, {}, "fill"),
        getCustomizedColor(color, { weight: 800 }, "text"),
        `text-${color}-800`,
        className
      )}
    />
  );
};

const FolderSidebarButton = ({
  isCollapsed,
  folder,
  selectedFolderId,
  onClickFolder,
  folders,
  hideOptions = false,
  localDecks,
  isFolderOpen,
  setIsFolderOpen,
}: {
  isCollapsed: boolean;
  folder: Folder;
  selectedFolderId: string;
  onClickFolder: (folderId: Folder["id"]) => void;
  folders: Folder[];
  hideOptions: boolean;
  localDecks: LocalDeck[];
  isFolderOpen: boolean;
  setIsFolderOpen: (isFolderOpen: boolean) => void;
}) => {
  const navigate = useNavigate();
  const [isActionsVisible, setIsActionsVisible] = useState(false);

  return (
    <Button
      key={folder.id}
      variant="strokeSecondary"
      size="sm"
      className={cn(
        "w-full p-2 h-10 text-xs items-center justify-start group",
        getCustomizedColor(folder.color, {
          weight: 100,
          pseudoClass: "focus",
        }),
        selectedFolderId === folder.id && [
          getCustomizedColor(folder.color, { weight: 100 }, "bg"),
          getCustomizedColor(folder.color, {}, "border"),
        ],
        "hover:!bg-secondary-100",
        "relative",
        "rounded-lg"
      )}
      onClick={() => {
        onClickFolder?.(folder.id);
      }}
    >
      <div
        className={cn(
          "rounded-md w-7 h-7 flex-none flex items-center justify-center text-title-950",
          "group/icon",
          "group-hover:border group-hover:border-secondary-200",
          "group-focus:border",
          getCustomizedColor(folder.color, { weight: 800 }, "border")
        )}
      >
        <ChevronDown
          className={cn(
            "w-5 h-5 group-hover/icon:block hidden",
            isFolderOpen ? "rotate-180" : ""
          )}
          onClick={(e) => {
            e.stopPropagation();
            setIsFolderOpen(!isFolderOpen);
          }}
        />
        {selectedFolderId === folder.id ? (
          <FolderColoredIcon
            color={folder.color}
            isOpen
            className="group-hover/icon:hidden"
          />
        ) : (
          <>
            <FolderColoredIcon
              color={folder.color}
              isOpen={false}
              className="group-hover:hidden group-hover/icon:hidden"
            />
            <FolderColoredIcon
              color={folder.color}
              isOpen={true}
              className=" hidden group-hover:block group-hover/icon:hidden"
            />
          </>
        )}
      </div>
      {!isCollapsed && (
        <span className="text-body-600 text-xs font-bold">{folder.name}</span>
      )}

      {!hideOptions && (
        <div
          className={cn(
            "absolute right-2 group-hover:bg-secondary-100 flex-row items-center gap-2",
            isActionsVisible ? "opacity-100" : "opacity-0",
            "group-hover:opacity-100 transition-opacity",
            "z-[1000]"
          )}
        >
          <DropdownFolderDialog
            folder={folder}
            folders={folders}
            localDecks={localDecks}
            folderId={folder.id}
            iconSize="icon2xs"
            onOpenChange={setIsActionsVisible}
          />
          <NewFolderDialog parentFolderId={folder.id} />
          <Button
            variant="strokeSecondary"
            size="icon2xs"
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/add` + (folder.id ? `?folder=${folder.id}` : ""));
            }}
          >
            <AddDeckIcon className="h-icon-sm w-icon-sm" />
          </Button>
        </div>
      )}
    </Button>
  );
};
